import { styled } from '@mui/material';
import PasswordRoleBasedRouting from 'app/PasswordRoleBasedRouting';
import ProviderWrapper from 'app/ProviderWrapper';
import axios from 'axios';
import Header from 'components/Header';
import LogoOnlyHeader from 'components/Header/LogoOnlyHeader';
import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Loading, Stack } from 'shell/components';
import { clearAuthToken } from 'utils/auth-storage';
import { buildAppPath } from 'utils/routing';

axios.defaults.baseURL = process.env.REACT_APP_API_ENDPOINT;
axios.interceptors.response.use(
    ({ data }) => data,
    ({ response = {} }) => {
        if (response.status === 401) {
            clearAuthToken();
            axios.defaults.headers.common.Authorization = undefined;
            if (window.location.pathname.endsWith('/session-expired')) {
                return Promise.reject(response);
            }
            window.location.replace(buildAppPath('/session-expired'));
        }
        return Promise.reject(response);
    }
);

const noHeaderRoutes = [
    '/accepted-invitation',
    '/components',
    '/course-set-up',
    '/access-denied',
    '/profile-setup',
    '/survey',
    '/account-settings',
];
const logoOnlyHeaderRoutes = ['/access-denied', '/session-expired'];
const publicAuthRoutes = [
    '/',
    '/login',
    '/sign-up',
    '/forgot-password',
    '/confirm-sign-up',
    '/update-password',
    '/federated-login',
    '/components',
];

const AppHeader = () => {
    const { auth } = useAuth();
    const location = useLocation();
    const { device } = useViewport();

    const isNoHeader = noHeaderRoutes.some((route) => {
        return location.pathname.endsWith(route);
    });
    const isLogoOnlyRoute = logoOnlyHeaderRoutes.includes(location.pathname);
    const isUnauthenticatedErrorRoute = !auth && !publicAuthRoutes.includes(location.pathname);
    const shouldShowLogoOnlyHeader = isLogoOnlyRoute || isUnauthenticatedErrorRoute;

    if (!shouldShowLogoOnlyHeader && (!auth || isNoHeader)) return null;

    return <HeaderWrapper device={device}>{shouldShowLogoOnlyHeader ? <LogoOnlyHeader /> : <Header />}</HeaderWrapper>;
};

const App = () => {
    const { loading } = useAuth();

    const renderRoute = () => {
        return <PasswordRoleBasedRouting />;
    };

    useEffect(() => {
        let observer;

        const updateTitle = () => {
            const h1Elements = document.querySelectorAll('h1');
            if (h1Elements.length === 1) {
                document.title = `${h1Elements[0].textContent} - TimelyGrader`;
            } else if (h1Elements.length > 1) {
                const titles = Array.from(h1Elements)
                    .slice(1)
                    .map((h1) => h1.textContent);
                document.title = `${titles.join(' & ')} - TimelyGrader`;
            }
        };

        observer = new MutationObserver(() => {
            updateTitle();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        updateTitle();

        return () => {
            if (observer) observer.disconnect();
        };
    }, []);

    return loading ? (
        <Loading />
    ) : (
        <ProviderWrapper>
            <PageWrapper>
                <AppHeader />
                <BodyWrapper data-app-scroll-container tabIndex={-1}>
                    {renderRoute()}
                </BodyWrapper>
            </PageWrapper>
        </ProviderWrapper>
    );
};

const PageWrapper = styled(Stack)(() => {
    return {
        height: '100%',
    };
});

const HeaderWrapper = styled(Box)(({ theme, device }) => {
    return {
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        background: theme.palette.ensightful.shades[0],
        ...(device !== 'mobile' && {
            zIndex: theme.zIndex.drawer + 1,
        }),
    };
});

const BodyWrapper = styled(Box)(({ theme }) => {
    return {
        flex: 1,
        overflow: 'auto',
        background: theme.palette.ensightful.neutral[50],
    };
});

export default App;
